import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Mail, Lock, TrendingUp, Users, MapPin, GraduationCap, DollarSign } from 'lucide-react';
import colors from '../config/colors';
import InlineContributionForm from '../components/InlineContributionForm';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SchoolReportPage = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState('tier1_only'); // tier1_only | tier2_unlocked
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [currentFeeChartTab, setCurrentFeeChartTab] = useState('secondary'); // primary | secondary
  const [schoolReviews, setSchoolReviews] = useState({}); // Store reviews for each school

  const loadReportData = useCallback(() => {
    try {
      const matches = JSON.parse(sessionStorage.getItem('school_matches') || '{}');
      const childName = sessionStorage.getItem('child_name') || 'your child';
      const questionnaireData = JSON.parse(sessionStorage.getItem('questionnaire_data') || '{}');

      if (!matches.top_schools || matches.top_schools.length === 0) {
        navigate('/questionnaire');
        return;
      }

      // Extract metadata from questionnaire
      const entryGrade = questionnaireData.child_profile?.current_grade || 7;
      const schoolLevel = questionnaireData.child_profile?.school_level || 'secondary';
      const currentSchool = questionnaireData.diagnostic_questions?.current_school || 'Not specified';
      const curriculumPriority = questionnaireData.diagnostic_questions?.curriculum_priority || 'Flexible';
      const budget = questionnaireData.diagnostic_questions?.budget || 'Not specified';

      setReportData({
        childName,
        entryGrade,
        schoolLevel,
        currentSchool,
        curriculumPriority,
        budget,
        dateGenerated: new Date().toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' }),
        matches: matches.top_schools.slice(0, 3), // Top 3 only
        nearMisses: matches.near_misses || [] // Schools that just missed the cut (from backend)
      });

      // Set default chart tab based on school level
      setCurrentFeeChartTab(schoolLevel);
      setLoading(false);
      
      // Load reviews for all 3 schools
      loadSchoolReviews(matches.top_schools.slice(0, 3));
    } catch (error) {
      // Error loading report - redirect to questionnaire
      navigate('/questionnaire');
    }
  }, [navigate]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const loadSchoolReviews = async (schools) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const reviewsData = {};

    for (const school of schools) {
      if (school.id) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/schools/${school.id}/reviews`);
          if (response.ok) {
            const reviews = await response.json();
            reviewsData[school.id] = reviews;
          }
        } catch (error) {
          console.error(`Failed to load reviews for ${school.full_name}:`, error);
        }
      }
    }

    setSchoolReviews(reviewsData);
  };

  const handleUnlockTier2 = async () => {
    // TODO: Integrate HitPay payment
    alert('Payment integration coming soon! For now, unlocking Tier 2 for testing.');
    setTier('tier2_unlocked');
  };

  const handleEmailReport = async () => {
    if (!email || !email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/report/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          report_data: reportData
        })
      });

      if (response.ok) {
        setEmailSent(true);
      } else {
        alert('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bgPage,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <p style={{ fontSize: '1.2rem', color: colors.textMuted }}>Loading your report...</p>
      </div>
    );
  }

  if (!reportData) return null;

  const topSchool = reportData.matches[0];
  const entryGradeStr = reportData.entryGrade.toString();
  const age = reportData.entryGrade + 5; // Rough age calculation

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bgPage,
      paddingTop: '40px',
      paddingBottom: '80px'
    }}>
      {/* Container */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
        
        {/* Report Header */}
        <ReportHeader reportData={reportData} age={age} />

        {/* Tier 1: Top School */}
        <div style={{ marginTop: '48px' }}>
          <SchoolCard 
            school={topSchool} 
            rank={1} 
            entryGrade={entryGradeStr}
            currentSchool={reportData.currentSchool}
            showFeeComparison={true}
            schoolLevel={reportData.schoolLevel}
            reviews={schoolReviews[topSchool.id]}
          />
        </div>

        {/* Upsell Block (if Tier 1 only) */}
        {tier === 'tier1_only' && reportData.matches.length > 1 && (
          <UpsellBlock onUnlock={handleUnlockTier2} />
        )}

        {/* Tier 2: Schools #2 and #3 (blurred if locked) */}
        {reportData.matches.length > 1 && (
          <div style={{
            marginTop: '32px',
            position: 'relative',
            filter: tier === 'tier1_only' ? 'blur(5px)' : 'none',
            pointerEvents: tier === 'tier1_only' ? 'none' : 'auto',
            opacity: tier === 'tier1_only' ? 0.5 : 1
          }}>
            {reportData.matches[1] && (
              <div style={{ marginBottom: '32px' }}>
                <SchoolCard 
                  school={reportData.matches[1]} 
                  rank={2} 
                  entryGrade={entryGradeStr}
                  currentSchool={reportData.currentSchool}
                  showFeeComparison={false}
                  schoolLevel={reportData.schoolLevel}
                  reviews={schoolReviews[reportData.matches[1].id]}
                />
              </div>
            )}
            {reportData.matches[2] && (
              <SchoolCard 
                school={reportData.matches[2]} 
                rank={3} 
                entryGrade={entryGradeStr}
                currentSchool={reportData.currentSchool}
                showFeeComparison={false}
                schoolLevel={reportData.schoolLevel}
                reviews={schoolReviews[reportData.matches[2].id]}
              />
            )}
          </div>
        )}

        {/* Tier 2: Fee Comparison Chart (all 3 schools) */}
        {tier === 'tier2_unlocked' && reportData.matches.length >= 3 && (
          <div style={{ marginTop: '48px' }}>
            <FeeComparisonChart 
              schools={reportData.matches} 
              currentSchool={reportData.currentSchool}
              schoolLevel={reportData.schoolLevel}
              currentTab={currentFeeChartTab}
              onTabChange={setCurrentFeeChartTab}
            />
          </div>
        )}

        {/* Inline Community Contribution Form */}
        {tier === 'tier2_unlocked' && reportData.matches.length >= 3 && (
          <InlineContributionForm 
            schools={reportData.matches}
            questionnaireData={JSON.parse(sessionStorage.getItem('questionnaire_data') || '{}')}
          />
        )}

        {/* Near Misses Section */}
        {tier === 'tier2_unlocked' && reportData.nearMisses.length > 0 && (
          <NearMissesSection nearMisses={reportData.nearMisses} priorities={reportData.priorities} />
        )}

        {/* Email Block */}
        <EmailBlock 
          email={email}
          setEmail={setEmail}
          onSend={handleEmailReport}
          emailSent={emailSent}
        />

        {/* Footer */}
        <ReportFooter />
      </div>
    </div>
  );
};

// ========== SUB-COMPONENTS ==========

const ReportHeader = ({ reportData, age }) => (
  <div style={{ textAlign: 'center', marginBottom: '32px' }}>
    <p style={{
      fontSize: '0.7rem',
      fontWeight: '600',
      color: colors.primary,
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      marginBottom: '12px'
    }}>
      SWITCH SCHOOLS — YOUR REPORT
    </p>
    <h1 style={{
      fontFamily: "'EB Garamond', serif",
      fontSize: '2.5rem',
      fontWeight: '400',
      color: colors.textPrimary,
      marginBottom: '16px',
      lineHeight: '1.2'
    }}>
      School matches for <span style={{ fontStyle: 'italic', color: colors.primary }}>{reportData.childName}</span>
    </h1>
    <p style={{
      fontSize: '0.85rem',
      color: colors.textMuted,
      lineHeight: '1.6'
    }}>
      Age {age} · Entering Year {reportData.entryGrade} · Currently at {reportData.currentSchool}
      <br />
      Looking for: {reportData.curriculumPriority} · Budget: {reportData.budget} · Generated {reportData.dateGenerated}
    </p>
  </div>
);

const SchoolCard = ({ school, rank, entryGrade, currentSchool, showFeeComparison, schoolLevel, reviews }) => {
  const matchScore = Math.round(school.match_score || 0);
  const schoolFee = school.annual_fees?.[entryGrade] || 0;

  return (
    <div style={{
      background: '#FFFFFF',
      border: `1px solid ${colors.bgMuted}`,
      borderRadius: '12px',
      padding: '32px',
      marginBottom: '24px'
    }}>
      {/* School Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: colors.primary,
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            fontSize: '0.9rem'
          }}>
            {rank}
          </div>
          <h2 style={{
            fontFamily: "'EB Garamond', serif",
            fontSize: '1.8rem',
            fontWeight: '500',
            color: colors.textPrimary,
            margin: 0
          }}>
            {school.full_name || school.common_name}
          </h2>
        </div>
        <p style={{
          fontSize: '0.9rem',
          color: colors.textMuted,
          margin: '8px 0 0 44px'
        }}>
          {school.campus_locations?.[0] || 'KL'} · {school.curriculum_framework || 'International'} · Est. {school.year_established || '—'}
        </p>
      </div>

      {/* Match Score */}
      <div style={{ marginBottom: '24px', paddingLeft: '44px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <span style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: colors.primary
          }}>
            {matchScore}%
          </span>
          <span style={{ fontSize: '0.9rem', color: colors.textMuted }}>match score</span>
        </div>
        <div style={{
          width: '100%',
          height: '6px',
          background: colors.bgMuted,
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${matchScore}%`,
            height: '100%',
            background: colors.primary,
            transition: 'width 0.5s ease'
          }} />
        </div>
      </div>

      {/* 6-cell Detail Grid */}
      <DetailGrid school={school} entryGrade={entryGrade} schoolFee={schoolFee} />

      {/* Strengths and Caveats */}
      <TagsSection school={school} reviews={reviews} />

      {/* Reviews Section */}
      {reviews && reviews.has_reviews && (
        <ReviewsSection reviews={reviews} />
      )}

      {/* Fee Comparison (Tier 1 only) */}
      {showFeeComparison && (
        <FeeComparisonTable 
          school={school} 
          currentSchool={currentSchool}
          entryGrade={entryGrade}
          schoolLevel={schoolLevel}
        />
      )}
    </div>
  );
};

const DetailGrid = ({ school, entryGrade, schoolFee }) => {
  // Determine if we should show exact fee or tier estimate
  const hasTierOnly = school.fee_tier && !school.fees_publicly_available;
  const feeDisplay = hasTierOnly && schoolFee === 0
    ? `${school.fee_tier_range} (est.)` 
    : `RM ${schoolFee.toLocaleString()}`;
  
  const details = [
    { label: 'Annual Fees (Year ' + entryGrade + ')', value: feeDisplay },
    { label: 'Curriculum', value: school.curriculum_framework || '—' },
    { label: 'Student:Teacher Ratio', value: '1:12' }, // TODO: Add to schema
    { label: 'Average Class Size', value: '22 students' }, // TODO: Add to schema
    { label: 'Language Support', value: school.mother_tongue_programmes?.join(', ') || 'English' },
    { label: 'Boarding', value: school.boarding_available ? 'Available' : 'Day school only' }
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
      marginBottom: '24px',
      paddingLeft: '44px'
    }}>
      {details.map((detail, idx) => (
        <div key={idx} style={{
          padding: '16px',
          background: colors.bgPage,
          borderRadius: '8px'
        }}>
          <p style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '6px'
          }}>
            {detail.label}
          </p>
          <p style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: colors.textPrimary
          }}>
            {detail.value}
          </p>
          {idx === 0 && hasTierOnly && (
            <p style={{
              fontSize: '0.7rem',
              color: colors.textMuted,
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              Exact fees not publicly listed
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const TagsSection = ({ school, reviews }) => {
  // Use reviews data if available, otherwise use defaults
  const strengths = reviews?.has_reviews ? reviews.pros.slice(0, 3) : [
    'Strong STEM programs',
    'Diverse community',
    'University counselling'
  ];
  
  const caveats = reviews?.has_reviews ? reviews.cons.slice(0, 2) : [
    'Waitlist 6+ months',
    'Limited sports facilities'
  ];

  return (
    <div style={{ marginBottom: '24px', paddingLeft: '44px' }}>
      {/* Strengths */}
      <div style={{ marginBottom: '12px' }}>
        <p style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: colors.textMuted,
          marginBottom: '8px'
        }}>
          STRENGTHS
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {strengths.map((tag, idx) => (
            <span key={idx} style={{
              padding: '6px 12px',
              background: '#D1FAE5',
              color: '#065F46',
              fontSize: '0.8rem',
              borderRadius: '16px',
              fontWeight: '500'
            }}>
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Caveats */}
      <div>
        <p style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: colors.textMuted,
          marginBottom: '8px'
        }}>
          CAVEATS
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {caveats.map((tag, idx) => (
            <span key={idx} style={{
              padding: '6px 12px',
              background: '#FEF3C7',
              color: '#92400E',
              fontSize: '0.8rem',
              borderRadius: '16px',
              fontWeight: '500'
            }}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const ReviewsSection = ({ reviews }) => {
  if (!reviews || !reviews.has_reviews) return null;

  return (
    <div style={{
      marginTop: '32px',
      marginBottom: '24px',
      paddingLeft: '44px',
      paddingTop: '24px',
      borderTop: `1px solid ${colors.bgMuted}`
    }}>
      {/* Overall Rating */}
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: colors.primary
          }}>
            {reviews.overall_rating?.toFixed(1)}
          </span>
          <span style={{ fontSize: '1.5rem', color: '#F59E0B' }}>★</span>
        </div>
        <div>
          <p style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '4px'
          }}>
            PARENT REVIEWS
          </p>
          <p style={{
            fontSize: '0.85rem',
            color: colors.textMuted
          }}>
            Based on {reviews.total_reviews} reviews from {reviews.sourced_from?.join(', ')}
          </p>
        </div>
      </div>

      {/* Summary */}
      <div style={{
        padding: '16px 20px',
        background: colors.bgPage,
        borderLeft: `4px solid ${colors.primary}`,
        borderRadius: '4px',
        marginBottom: '24px'
      }}>
        <p style={{
          fontSize: '0.95rem',
          lineHeight: '1.6',
          color: colors.textPrimary,
          fontStyle: 'italic',
          margin: 0
        }}>
          {reviews.summary}
        </p>
      </div>

      {/* Parent Quotes */}
      {reviews.parent_quotes && reviews.parent_quotes.length > 0 && (
        <div>
          <p style={{
            fontSize: '0.85rem',
            fontWeight: '600',
            color: colors.textMuted,
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            WHAT PARENTS SAY
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {reviews.parent_quotes.slice(0, 3).map((quote, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: '#FFFFFF',
                border: `1px solid ${colors.bgMuted}`,
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <span style={{
                    fontSize: '2rem',
                    color: colors.bgMuted,
                    lineHeight: '1',
                    marginTop: '-8px'
                  }}>
                    "
                  </span>
                  <div style={{ flex: 1 }}>
                    <p style={{
                      fontSize: '0.9rem',
                      lineHeight: '1.6',
                      color: colors.textPrimary,
                      marginBottom: '8px'
                    }}>
                      {quote.text}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {[...Array(5)].map((_, i) => (
                          <span key={i} style={{
                            color: i < quote.rating ? '#F59E0B' : colors.bgMuted,
                            fontSize: '0.9rem'
                          }}>
                            ★
                          </span>
                        ))}
                      </div>
                      <span style={{
                        fontSize: '0.75rem',
                        color: colors.textMuted
                      }}>
                        — {quote.source}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FeeComparisonTable = ({ school, currentSchool, entryGrade, schoolLevel }) => {
  const grades = schoolLevel === 'primary' ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11];
  const hasCurrentSchoolData = currentSchool !== 'Not specified' && currentSchool !== 'none';
  const hasTierOnly = school.fee_tier && !school.fees_publicly_available;

  return (
    <div style={{ marginTop: '32px', paddingLeft: '44px' }}>
      <h3 style={{
        fontSize: '1.2rem',
        fontWeight: '600',
        color: colors.textPrimary,
        marginBottom: '16px'
      }}>
        Fee Comparison
      </h3>
      
      {hasTierOnly && (
        <div style={{
          padding: '12px 16px',
          background: '#FEF3C7',
          border: '1px solid #FCD34D',
          borderRadius: '6px',
          marginBottom: '16px',
          fontSize: '0.85rem',
          color: '#92400E'
        }}>
          💡 <strong>{school.full_name}</strong> fees are estimated as <strong>{school.fee_tier_range}</strong> per year (exact fees not publicly listed)
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem'
        }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.bgMuted}` }}>
              <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600', color: colors.textMuted }}>Grade</th>
              {hasCurrentSchoolData && (
                <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: '600', color: colors.textMuted }}>Current School</th>
              )}
              <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: '600', color: colors.textMuted }}>New School</th>
              {hasCurrentSchoolData && (
                <>
                  <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: '600', color: colors.textMuted }}>Difference</th>
                  <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: '600', color: colors.textMuted }}>Change</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {grades.map((grade, idx) => {
              const newFee = school.annual_fees?.[grade.toString()] || 0;
              const currentFee = 50000; // Mock - TODO: Get from current school
              const diff = newFee - currentFee;
              const changePercent = currentFee > 0 ? ((diff / currentFee) * 100).toFixed(1) : 0;

              return (
                <tr key={grade} style={{
                  borderBottom: `1px solid ${colors.bgMuted}`,
                  background: idx % 2 === 0 ? '#FFFFFF' : colors.bgPage
                }}>
                  <td style={{ padding: '12px 8px', fontWeight: '500' }}>Year {grade}</td>
                  {hasCurrentSchoolData && (
                    <td style={{ textAlign: 'right', padding: '12px 8px', color: colors.textMuted }}>
                      RM {currentFee.toLocaleString()}
                    </td>
                  )}
                  <td style={{ textAlign: 'right', padding: '12px 8px', fontWeight: '600' }}>
                    RM {newFee.toLocaleString()}
                  </td>
                  {hasCurrentSchoolData && (
                    <>
                      <td style={{
                        textAlign: 'right',
                        padding: '12px 8px',
                        color: diff > 0 ? colors.primary : '#059669',
                        fontWeight: '600'
                      }}>
                        {diff > 0 ? '+' : ''}RM {Math.abs(diff).toLocaleString()}
                      </td>
                      <td style={{
                        textAlign: 'right',
                        padding: '12px 8px',
                        color: diff > 0 ? colors.primary : '#059669',
                        fontWeight: '500'
                      }}>
                        {diff > 0 ? '+' : ''}{changePercent}%
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!hasCurrentSchoolData && (
        <p style={{
          fontSize: '0.8rem',
          color: colors.textMuted,
          marginTop: '12px',
          fontStyle: 'italic'
        }}>
          Your current school's fees are not in our database. Showing projected fees at {school.full_name} only.
        </p>
      )}
    </div>
  );
};

const UpsellBlock = ({ onUnlock }) => (
  <div style={{
    position: 'relative',
    marginTop: '48px',
    padding: '40px 32px',
    background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
    border: `2px solid ${colors.primary}`,
    borderRadius: '12px',
    textAlign: 'center'
  }}>
    <Lock size={32} color={colors.primary} style={{ marginBottom: '16px' }} />
    <h3 style={{
      fontSize: '1.5rem',
      fontWeight: '600',
      color: colors.textPrimary,
      marginBottom: '8px'
    }}>
      Unlock schools #2 and #3
    </h3>
    <p style={{
      fontSize: '1rem',
      color: colors.primary,
      fontWeight: '500',
      marginBottom: '16px'
    }}>
      + full fee comparison for all 3 schools
    </p>
    <p style={{
      fontSize: '0.9rem',
      color: colors.textMuted,
      marginBottom: '24px',
      maxWidth: '500px',
      margin: '0 auto 24px'
    }}>
      A-level results, university pathways, year-by-year fee tables, and side-by-side comparison for every shortlisted school.
    </p>
    <button
      onClick={onUnlock}
      style={{
        padding: '14px 32px',
        background: colors.primary,
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        marginBottom: '12px'
      }}
      onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
      onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
    >
      Unlock full report · RM 10
    </button>
    <p style={{
      fontSize: '0.75rem',
      color: colors.textMuted
    }}>
      One-time payment · Instant access · 24h unlock
    </p>
  </div>
);

const FeeComparisonChart = ({ schools, currentSchool, schoolLevel, currentTab, onTabChange }) => {
  const grades = currentTab === 'primary' ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11];

  const chartData = {
    labels: grades.map(g => `Year ${g}`),
    datasets: [
      {
        label: schools[0]?.full_name || 'School 1',
        data: grades.map(g => schools[0]?.annual_fees?.[g.toString()] || 0),
        backgroundColor: '#E8612A',
      },
      {
        label: schools[1]?.full_name || 'School 2',
        data: grades.map(g => schools[1]?.annual_fees?.[g.toString()] || 0),
        backgroundColor: '#2A7A9E',
      },
      {
        label: schools[2]?.full_name || 'School 3',
        data: grades.map(g => schools[2]?.annual_fees?.[g.toString()] || 0),
        backgroundColor: '#6A4C93',
      }
    ]
  };

  return (
    <div style={{
      background: '#FFFFFF',
      padding: '32px',
      borderRadius: '12px',
      border: `1px solid ${colors.bgMuted}`
    }}>
      <h3 style={{
        fontSize: '1.4rem',
        fontWeight: '600',
        color: colors.textPrimary,
        marginBottom: '16px'
      }}>
        Fee Comparison (All 3 Schools)
      </h3>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <button
          onClick={() => onTabChange('primary')}
          style={{
            padding: '8px 16px',
            background: currentTab === 'primary' ? colors.primary : colors.bgPage,
            color: currentTab === 'primary' ? '#FFFFFF' : colors.textMuted,
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Primary (Y1-6)
        </button>
        <button
          onClick={() => onTabChange('secondary')}
          style={{
            padding: '8px 16px',
            background: currentTab === 'secondary' ? colors.primary : colors.bgPage,
            color: currentTab === 'secondary' ? '#FFFFFF' : colors.textMuted,
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Secondary (Y7-11)
        </button>
      </div>

      {/* Chart */}
      <div style={{ height: '350px' }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: 'top'
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    return context.dataset.label + ': RM ' + context.parsed.y.toLocaleString();
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return 'RM ' + (value / 1000) + 'k';
                  }
                }
              },
              x: {
                ticks: {
                  autoSkip: false
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

const NearMissesSection = ({ nearMisses, priorities }) => (
  <div style={{
    marginTop: '48px',
    padding: '32px',
    background: '#FFFFFF',
    borderRadius: '12px',
    border: `1px solid ${colors.bgMuted}`
  }}>
    <h3 style={{
      fontSize: '1.4rem',
      fontWeight: '600',
      color: colors.textPrimary,
      marginBottom: '24px'
    }}>
      Close, but didn't make the shortlist
    </h3>

    {nearMisses.slice(0, 2).map((school, idx) => (
      <div key={idx} style={{
        marginBottom: '24px',
        paddingBottom: '24px',
        borderBottom: idx === 0 ? `1px solid ${colors.bgMuted}` : 'none'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h4 style={{
            fontSize: '1.2rem',
            fontWeight: '600',
            color: colors.textPrimary,
            margin: 0
          }}>
            {school.full_name}
          </h4>
          <span style={{
            fontSize: '0.9rem',
            color: colors.textMuted
          }}>
            {Math.round(school.match_score || 0)}/100
          </span>
        </div>

        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0
        }}>
          <li style={{
            fontSize: '0.9rem',
            color: colors.textMuted,
            marginBottom: '8px',
            paddingLeft: '20px',
            position: 'relative'
          }}>
            <span style={{ position: 'absolute', left: 0, color: colors.primary }}>—</span>
            Fees (RM {school.annual_fees?.['7'] || 0}/year) exceed your budget
          </li>
          <li style={{
            fontSize: '0.9rem',
            color: colors.textMuted,
            paddingLeft: '20px',
            position: 'relative'
          }}>
            <span style={{ position: 'absolute', left: 0, color: colors.primary }}>—</span>
            Waitlist of 18+ months; entry unlikely by target date
          </li>
        </ul>
      </div>
    ))}
  </div>
);

const EmailBlock = ({ email, setEmail, onSend, emailSent }) => (
  <div style={{
    marginTop: '64px',
    padding: '40px 32px',
    background: colors.bgPage,
    borderRadius: '12px',
    textAlign: 'center'
  }}>
    <h3 style={{
      fontFamily: "'EB Garamond', serif",
      fontSize: '1.8rem',
      fontWeight: '400',
      color: colors.textPrimary,
      marginBottom: '16px'
    }}>
      Get this report in your inbox
    </h3>

    {!emailSent ? (
      <div style={{
        display: 'flex',
        gap: '12px',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          style={{
            flex: 1,
            padding: '12px 16px',
            border: `1px solid ${colors.bgMuted}`,
            borderRadius: '8px',
            fontSize: '1rem'
          }}
        />
        <button
          onClick={onSend}
          style={{
            padding: '12px 24px',
            background: colors.primary,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Send report
        </button>
      </div>
    ) : (
      <p style={{
        fontSize: '1.1rem',
        color: '#059669',
        fontWeight: '600'
      }}>
        ✓ Report sent!
      </p>
    )}
  </div>
);

const ReportFooter = () => (
  <div style={{
    marginTop: '64px',
    paddingTop: '32px',
    borderTop: `1px solid ${colors.bgMuted}`,
    textAlign: 'center'
  }}>
    <p style={{
      fontSize: '0.85rem',
      color: colors.textMuted,
      marginBottom: '8px'
    }}>
      Switch Schools · switchschools.my
    </p>
    <p style={{
      fontSize: '0.75rem',
      color: colors.textMuted
    }}>
      Fee data sourced from school websites. Always verify directly with the school admissions office.
    </p>
  </div>
);

export default SchoolReportPage;
